/**
 * Authentication Module
 * Handles phone/Google login and profile completion
 */
import { AuthAPI, EventsAPI, OtpAPI } from '../services/api.js';
import { AppState, updateState } from '../config.js';
import { showToast, AuthScreens, updateUserName, hideSplash } from '../utils/ui.js';
import { connectSocket, disconnectSocket } from '../services/socket.js';
import { requestPushPermission, refreshTokenIfNeeded, setupForegroundPushListener, onForegroundMessage } from '../services/push.js';
import { t } from '../utils/i18n.js';
import { haptic } from '../utils/haptics.js';
import { getFingerprint } from '../services/device-id.js';

// In-memory invite code for already-authenticated fast path
let _pendingInviteCode = null;

// Phone token from OTP verification
let _phoneToken = null;

/**
 * Initialize authentication module
 */
export function initAuth(savedPage = 'home', oauth = {}) {
    const urlParams = new URLSearchParams(window.location.search);

    // Pick up join code from query param (?join=CODE, from backend redirect)
    // or from localStorage (set earlier by socialswipe.js from /join/:code path)
    const joinParam = urlParams.get('join');
    if (joinParam) {
        localStorage.setItem('pendingInvite', joinParam);
    }

    // Load pending invite into memory for fast-path (avoids extra localStorage read)
    const pending = localStorage.getItem('pendingInvite');
    if (pending) {
        _pendingInviteCode = pending;
    }

    setupAuthEventListeners();
    setupForegroundPushListener();
    checkAuthStatus(savedPage, oauth);
}

/**
 * Show invite banner on auth screens when a pending invite exists.
 * Fetches event info from the public endpoint and displays a welcome message.
 */
async function showInviteBanner() {
    const inviteCode = _pendingInviteCode || localStorage.getItem('pendingInvite');
    if (!inviteCode) return;

    try {
        const response = await EventsAPI.getInviteInfo(inviteCode);
        if (!response.success) {
            if (response.message === 'This event has already passed') {
                // Clear invite code — no point trying to join after login
                localStorage.removeItem('pendingInvite');
                _pendingInviteCode = null;
                const banner = document.getElementById('invite-banner-login');
                const title = document.getElementById('invite-banner-login-title');
                if (banner && title) {
                    title.textContent = t('invite.eventPassedBanner');
                    banner.classList.remove('hidden');
                    banner.classList.add('invite-banner--ended');
                }
            }
            return;
        }

        const eventName = response.event.name;
        const bannerText = t('invite.invitedTo', { name: eventName });

        const banner = document.getElementById('invite-banner-login');
        const title = document.getElementById('invite-banner-login-title');
        if (banner && title) {
            title.textContent = bannerText;
            banner.classList.remove('hidden');
        }
    } catch (error) {
        console.error('Failed to fetch invite info:', error);
    }
}

/**
 * Process a pending invite code after successful authentication.
 * Reads from localStorage (survives page reloads) or in-memory variable (fast path).
 */
async function processPendingInvite() {
    const inviteCode = _pendingInviteCode || localStorage.getItem('pendingInvite');
    if (!inviteCode) return;
    console.log('[Join] Processing join code:', inviteCode);

    // Clean the join param from URL (safe — code is still in localStorage/_pendingInviteCode)
    const url = new URL(window.location.href);
    if (url.searchParams.has('join')) {
        url.searchParams.delete('join');
        window.history.replaceState({}, document.title, url.pathname + url.search);
    }

    try {
        showToast(t('invite.joiningViaInvite'), 'info', true);
        const response = await EventsAPI.joinViaInvite(inviteCode);

        // Clear invite code only after we have a definitive outcome
        localStorage.removeItem('pendingInvite');
        _pendingInviteCode = null;

        if (response.success) {
            showToast(t('invite.joinedViaInvite', { name: response.event.name }), 'success', true);
            const { handleEventClick } = await import('./home.js');
            await handleEventClick(response.event.id);
        } else if (response.message === 'You are already participating in this event') {
            showToast(response.message, 'info', true);
            const { handleEventClick } = await import('./home.js');
            await handleEventClick(response.event?.id);
        } else if (response.message === 'Cannot join past events') {
            showToast(t('invite.eventPassed'), 'error');
        } else {
            showToast(response.message || t('invite.invalidInvite'), 'error');
        }
    } catch (error) {
        console.error('Failed to process invite code:', error);
        showToast(error.message || t('invite.invalidInvite'), 'error');
        // Don't clear invite code on network error — user can retry by refreshing
    }
}

/**
 * Complete the post-auth flow: connect socket, load home, process invites
 */
async function enterApp(savedPage = 'home') {
    AuthScreens.showMainContent();
    connectSocket();
    requestPushPermission()
        .then(() => {
            onForegroundMessage((notification) => {
                const title = notification.title || '';
                const body = notification.body || '';
                showToast(body || title || 'New notification', 'info', true);
            });
        })
        .catch(err => console.log('Push permission:', err));
    refreshTokenIfNeeded().catch(err => console.log('FCM refresh:', err));

    if (savedPage !== 'home' && window.navigateToPage) {
        window.navigateToPage(savedPage);
    } else {
        const { loadHomeData } = await import('./home.js');
        loadHomeData();
    }

    processPendingInvite();
    hideSplash();

    // Fetch notification badge count and handle pending notification deep-link
    import('./notifications.js').then(async ({ fetchUnreadCount, handlePushNotificationClick }) => {
        fetchUnreadCount();

        // Check if app was opened from a push notification (URL params set by service worker)
        const params = new URLSearchParams(window.location.search);
        const ntype = params.get('ntype');
        if (ntype) {
            const notifId = params.get('notifId');
            const matchId = params.get('matchId');
            const eventId = params.get('eventId');

            // Clean URL params immediately
            const url = new URL(window.location);
            url.searchParams.delete('ntype');
            url.searchParams.delete('matchId');
            url.searchParams.delete('eventId');
            url.searchParams.delete('notifId');
            window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);

            // If we have a notifId, fetch the full notification from the API (includes title/body)
            let notifData = { type: ntype, matchId, eventId, notificationId: notifId };
            if (notifId) {
                try {
                    const { NotificationsAPI } = await import('../services/api.js');
                    const resp = await NotificationsAPI.getById(notifId);
                    if (resp.success && resp.notification) {
                        notifData = {
                            ...resp.notification,
                            notificationId: notifId
                        };
                    }
                } catch (e) { /* proceed with basic data */ }
            }

            // Small delay to let the app fully render first
            setTimeout(() => handlePushNotificationClick(notifData), 300);
        }
    }).catch(() => {});
}

/**
 * Check if user profile is complete and route accordingly
 */
function checkProfileAndRoute(user, savedPage = 'home') {
    updateState({ currentUser: user });
    localStorage.setItem('currentUser', JSON.stringify(user));
    updateUserName(user);

    if (!user.profileComplete) {
        // Pre-fill complete-profile form with any existing data (e.g. from Google)
        prefillCompleteProfile(user);
        AuthScreens.showCompleteProfile();
        hideSplash();
    } else {
        enterApp(savedPage);
    }
}

/**
 * Pre-fill the complete-profile form with existing user data
 */
function prefillCompleteProfile(user) {
    const firstNameInput = document.getElementById('complete-firstname');
    const lastNameInput = document.getElementById('complete-lastname');
    const birthdateInput = document.getElementById('complete-birthdate');
    const genderSelector = document.getElementById('complete-gender-selector');

    if (firstNameInput && user.firstName) firstNameInput.value = user.firstName;
    if (lastNameInput && user.lastName) lastNameInput.value = user.lastName;
    if (birthdateInput && user.birthdate) birthdateInput.value = user.birthdate;
    if (genderSelector && user.gender) {
        genderSelector.querySelectorAll('.gender-btn').forEach(btn => {
            btn.classList.toggle('selected', btn.getAttribute('data-gender') === user.gender);
        });
    }
}

/**
 * Check current authentication status
 */
export async function checkAuthStatus(savedPage = 'home', oauth = {}) {
    // Use pre-captured OAuth params (captured before initHistory stripped query params)
    // or fall back to reading from current URL
    const urlParams = new URLSearchParams(window.location.search);
    const oauthToken = oauth.oauthToken || urlParams.get('token');
    const oauthError = oauth.oauthError || urlParams.get('error');

    if (oauthError) {
        // OAuth failure redirect detected
        console.log('OAuth error detected:', oauthError);
        const url = new URL(window.location.href);
        url.searchParams.delete('error');
        window.history.replaceState({}, document.title, url.pathname);
        showToast(t('auth.socialLoginFailed'), 'error');
        AuthScreens.showLogin();
        hideSplash();
        return;
    }

    if (oauthToken) {
        // OAuth redirect detected - handle the token
        console.log('OAuth token detected in URL');
        await handleOAuthToken(oauthToken, savedPage);
        return;
    }

    const savedUser = localStorage.getItem("currentUser");

    // If we have a saved user, try to restore the session.
    // The access token is memory-only; apiRequest will auto-refresh
    // it via the httpOnly refresh token cookie if needed.
    if (savedUser) {
        try {
            const fingerprint = await getFingerprint();
            const response = await AuthAPI.verifyToken(fingerprint);
            if (response.success) {
                checkProfileAndRoute(response.user, savedPage);
                return;
            }
        } catch (error) {
            console.log('Token verification failed');
        }
    }

    localStorage.removeItem('currentUser');
    AuthScreens.showLogin();
    hideSplash();

    // Show invite banner if user arrived via invite link
    showInviteBanner();

    // Activate quick-entry mode when coming from an invite/QR context
    if (_pendingInviteCode || localStorage.getItem('pendingInvite')) {
        document.querySelectorAll('.auth-screen').forEach(el => {
            el.classList.add('quick-entry-mode');
        });
        // Show the quick entry badge next to Google button
        document.querySelectorAll('.quick-entry-badge').forEach(el => {
            el.classList.remove('hidden');
        });
    }
}

/**
 * Handle OAuth token from URL redirect
 */
async function handleOAuthToken(token, savedPage = 'home') {
    try {
        // Save access token to memory only (refresh token is in httpOnly cookie)
        updateState({ authToken: token });

        // Verify the token and get user data (also sends fingerprint)
        const fingerprint = await getFingerprint();
        const response = await AuthAPI.verifyToken(fingerprint);

        if (response.success) {
            showToast(t('auth.oauthSuccess'), 'success');

            // Clean up the URL (remove token and refreshToken parameters)
            const url = new URL(window.location.href);
            url.searchParams.delete('token');
            url.searchParams.delete('refreshToken');
            window.history.replaceState({}, document.title, url.pathname);

            // Check profile completeness and route
            checkProfileAndRoute(response.user, savedPage);
        } else {
            throw new Error(response.message || 'Token verification failed');
        }
    } catch (error) {
        console.error('OAuth login error:', error);
        showToast(error.message || t('auth.loginFailed'), 'error');

        // Clean up failed authentication
        localStorage.removeItem('currentUser');
        updateState({ authToken: null, currentUser: null });

        // Clean up URL and show login screen
        const url = new URL(window.location.href);
        url.searchParams.delete('token');
        window.history.replaceState({}, document.title, url.pathname);

        AuthScreens.showLogin();
        hideSplash();
    }
}

/**
 * Setup authentication event listeners
 */
let authListenersInitialized = false;

function setupAuthEventListeners() {
    if (authListenersInitialized) return;
    authListenersInitialized = true;

    // Complete profile form submission
    const completeProfileForm = document.getElementById('complete-profile-form');
    if (completeProfileForm) completeProfileForm.addEventListener('submit', handleCompleteProfile);

    // Social login buttons (Google)
    document.querySelectorAll('.social-btn.google').forEach(btn => {
        btn.addEventListener('click', () => handleSocialLogin('google'));
    });

    // Logout button
    const logoutBtn = document.getElementById('logout-btn');
    if (logoutBtn) logoutBtn.addEventListener('click', logout);

    // Phone OTP verification on auth screen
    setupPhoneAuth();

    // Gender toggle for complete-profile
    setupGenderSelector('complete-gender-selector');
}

/**
 * Setup phone auth flow on the login screen
 */
function setupPhoneAuth() {
    const sendBtn = document.getElementById('auth-otp-send-btn');
    const verifyBtn = document.getElementById('auth-otp-verify-btn');
    const resendBtn = document.getElementById('auth-otp-resend-btn');
    const phoneInput = document.getElementById('auth-phone');
    const codeInput = document.getElementById('auth-otp-code-input');
    const verifySection = document.getElementById('auth-otp-verify-section');
    const successSection = document.getElementById('auth-otp-success');
    const timerEl = document.getElementById('auth-otp-timer');
    if (!sendBtn || !phoneInput) return;

    let cooldownTimer = null;
    let expiryTimer = null;

    // Country code selector state
    let selectedCountryCode = '+972';
    const countryBtn = document.getElementById('country-code-btn');
    const countryFlag = document.getElementById('country-flag');
    const countryDial = document.getElementById('country-dial');
    const dropdown = document.getElementById('country-code-dropdown');
    const countryList = document.getElementById('country-code-list');
    const countrySearch = document.getElementById('country-search');

    // Load country codes and setup selector
    setupCountryCodeSelector();

    async function setupCountryCodeSelector() {
        if (!countryBtn || !dropdown || !countryList) return;

        let countries = [];
        try {
            const resp = await OtpAPI.getCountryCodes();
            if (resp.success) countries = resp.countryCodes;
        } catch {
            // Fallback: just use Israel
            countries = [{ code: 'IL', dial: '+972', flag: '\u{1F1EE}\u{1F1F1}', name: 'Israel' }];
        }

        function renderList(filter = '') {
            countryList.innerHTML = '';
            const query = filter.toLowerCase();
            const filtered = countries.filter(c =>
                c.name.toLowerCase().includes(query) ||
                c.dial.includes(query) ||
                c.code.toLowerCase().includes(query)
            );
            for (const c of filtered) {
                const li = document.createElement('li');
                li.innerHTML = `<span class="cc-flag">${c.flag}</span><span class="cc-name">${c.name}</span><span class="cc-dial">${c.dial}</span>`;
                li.addEventListener('click', () => {
                    selectedCountryCode = c.dial;
                    countryFlag.textContent = c.flag;
                    countryDial.textContent = c.dial;
                    if (c.placeholder) {
                        phoneInput.placeholder = c.placeholder;
                    }
                    dropdown.classList.add('hidden');
                    countrySearch.value = '';
                    phoneInput.focus();
                });
                countryList.appendChild(li);
            }
        }

        renderList();

        countryBtn.addEventListener('click', () => {
            dropdown.classList.toggle('hidden');
            if (!dropdown.classList.contains('hidden')) {
                countrySearch.focus();
            }
        });

        countrySearch.addEventListener('input', () => renderList(countrySearch.value));

        // Close dropdown when clicking outside
        document.addEventListener('click', (e) => {
            if (!dropdown.contains(e.target) && !countryBtn.contains(e.target)) {
                dropdown.classList.add('hidden');
                countrySearch.value = '';
                renderList();
            }
        });
    }

    function getCountryCode() {
        return selectedCountryCode;
    }

    function startCooldown(seconds) {
        if (resendBtn) resendBtn.disabled = true;
        let remaining = seconds;
        if (resendBtn) resendBtn.textContent = t('auth.resendIn', { seconds: remaining });
        cooldownTimer = setInterval(() => {
            remaining--;
            if (remaining <= 0) {
                clearInterval(cooldownTimer);
                if (resendBtn) {
                    resendBtn.disabled = false;
                    resendBtn.innerHTML = `<span data-i18n="auth.resendCode">${t('auth.resendCode')}</span>`;
                }
            } else {
                if (resendBtn) resendBtn.textContent = t('auth.resendIn', { seconds: remaining });
            }
        }, 1000);
    }

    function startExpiryCountdown(seconds) {
        let remaining = seconds;
        expiryTimer = setInterval(() => {
            remaining--;
            const mins = Math.floor(remaining / 60);
            const secs = remaining % 60;
            if (timerEl) timerEl.textContent = `${mins}:${String(secs).padStart(2, '0')}`;
            if (remaining <= 0) {
                clearInterval(expiryTimer);
                if (timerEl) timerEl.textContent = t('auth.codeExpired');
            }
        }, 1000);
    }

    async function handleSendOtp() {
        const phone = phoneInput.value.trim();
        if (!phone) {
            showToast(t('auth.fillAllFields'), 'error');
            return;
        }

        sendBtn.disabled = true;
        sendBtn.innerHTML = '<i class="fas fa-spinner fa-spin me-2"></i> ...';

        try {
            const result = await OtpAPI.send(phone, 'login', getCountryCode());
            if (result.success) {
                showToast(t('auth.codeSent'), 'success');
                verifySection.classList.remove('hidden');
                sendBtn.classList.add('hidden');
                phoneInput.readOnly = true;
                phoneInput.style.opacity = '0.6';
                countryBtn.disabled = true;
                countryBtn.style.opacity = '0.6';
                codeInput.focus();
                startCooldown(60);
                startExpiryCountdown(result.expiresIn || 300);
            } else {
                const msg = result.message
                    || (result.errors && result.errors[0] && result.errors[0].msg)
                    || t('auth.genericError');
                showToast(msg, 'error');
                sendBtn.disabled = false;
                sendBtn.innerHTML = `<i class="fas fa-paper-plane me-2"></i><span data-i18n="auth.sendCode">${t('auth.sendCode')}</span>`;
            }
        } catch (err) {
            showToast(t('auth.genericError'), 'error');
            sendBtn.disabled = false;
            sendBtn.innerHTML = `<i class="fas fa-paper-plane me-2"></i><span data-i18n="auth.sendCode">${t('auth.sendCode')}</span>`;
        }
    }

    async function handleVerifyOtp() {
        const phone = phoneInput.value.trim();
        const code = codeInput.value.trim();
        if (!code || code.length !== 6) {
            showToast(t('auth.invalidCode'), 'error');
            return;
        }

        verifyBtn.disabled = true;
        verifyBtn.innerHTML = '<i class="fas fa-spinner fa-spin me-2"></i> ...';

        try {
            const result = await OtpAPI.verify(phone, code, getCountryCode());
            if (result.success && result.phoneToken) {
                _phoneToken = result.phoneToken;
                clearInterval(expiryTimer);
                clearInterval(cooldownTimer);
                verifySection.classList.add('hidden');
                successSection.classList.remove('hidden');
                haptic('success');

                // Now login with the phone token
                await handlePhoneLogin(phone, _phoneToken);
            } else {
                showToast(result.message || t('auth.invalidCode'), 'error');
                verifyBtn.disabled = false;
                verifyBtn.innerHTML = `<span data-i18n="auth.verifyCode">${t('auth.verifyCode')}</span>`;
                codeInput.value = '';
                codeInput.focus();
            }
        } catch (err) {
            showToast(t('auth.genericError'), 'error');
            verifyBtn.disabled = false;
            verifyBtn.innerHTML = `<span data-i18n="auth.verifyCode">${t('auth.verifyCode')}</span>`;
        }
    }

    function handleResend() {
        // Reset UI for new code
        clearInterval(expiryTimer);
        if (timerEl) timerEl.textContent = '';
        codeInput.value = '';
        handleSendOtp();
    }

    sendBtn.addEventListener('click', handleSendOtp);
    if (verifyBtn) verifyBtn.addEventListener('click', handleVerifyOtp);
    if (resendBtn) resendBtn.addEventListener('click', handleResend);

    // Allow Enter key in code input to trigger verify
    if (codeInput) {
        codeInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                handleVerifyOtp();
            }
        });
    }

    // Allow Enter key in phone input to trigger send
    if (phoneInput) {
        phoneInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                handleSendOtp();
            }
        });
    }
}

/**
 * Handle phone login after OTP verification
 */
async function handlePhoneLogin(phone, phoneToken) {
    try {
        const fingerprint = await getFingerprint();
        const response = await AuthAPI.phoneLogin(phone, phoneToken, fingerprint);

        if (response.success) {
            updateState({ authToken: response.token });
            showToast(t('auth.loginSuccess'), 'success');
            checkProfileAndRoute(response.user);
        } else {
            handleAuthError(response);
        }
    } catch (error) {
        console.error('Phone login error:', error);
        showToast(error.message || t('auth.loginFailed'), 'error');
    }
}

/**
 * Handle complete profile form submission
 */
async function handleCompleteProfile(event) {
    event.preventDefault();

    const firstName = document.getElementById('complete-firstname').value.trim();
    const lastName = document.getElementById('complete-lastname').value.trim();
    const genderBtn = document.querySelector('#complete-gender-selector .gender-btn.selected');
    const gender = genderBtn ? genderBtn.getAttribute('data-gender') : null;
    const birthdate = document.getElementById('complete-birthdate').value;
    const agreeTerms = document.getElementById('complete-agree-terms').checked;

    if (!firstName || !lastName) {
        showToast(t('auth.fillAllFields'), 'error');
        return;
    }

    if (!gender) {
        showToast(t('auth.selectGender'), 'error');
        return;
    }

    if (!birthdate) {
        showToast(t('auth.fillAllFields'), 'error');
        return;
    }

    // Validate 18+
    const birth = new Date(birthdate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
    if (age < 18) {
        showToast(t('auth.mustBe18'), 'error');
        return;
    }

    if (!agreeTerms) {
        showToast(t('auth.agreeTermsRequired'), 'error');
        return;
    }

    try {
        const response = await AuthAPI.completeProfile({
            firstName,
            lastName,
            gender,
            birthdate
        });

        if (response.success) {
            updateState({ currentUser: response.user });
            localStorage.setItem('currentUser', JSON.stringify(response.user));
            updateUserName(response.user);

            // Show celebration screen with confetti
            haptic('celebration');
            showConfetti();
            const form = document.getElementById('complete-profile-form');
            const celebration = document.getElementById('register-celebration');
            if (form && celebration) {
                form.style.display = 'none';
                celebration.classList.remove('hidden');
            }

            // After celebration, transition to main app
            setTimeout(async () => {
                enterApp();
            }, 2000);
        } else {
            handleAuthError(response);
        }
    } catch (error) {
        console.error('Complete profile error:', error);
        showToast(error.message || t('auth.registrationFailed'), 'error');
    }
}

/**
 * Setup gender selector toggle buttons
 */
function setupGenderSelector(selectorId) {
    const selector = document.getElementById(selectorId);
    if (!selector) return;

    selector.querySelectorAll('.gender-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            selector.querySelectorAll('.gender-btn').forEach(b => b.classList.remove('selected'));
            btn.classList.add('selected');
            haptic('light');
        });
    });
}

/**
 * Show confetti celebration
 */
function showConfetti() {
    const container = document.createElement('div');
    container.style.cssText = 'position:fixed;inset:0;z-index:9999;pointer-events:none;overflow:hidden';

    const colors = ['#FF00FF', '#00FFFF', '#FFD700', '#FF69B4', '#7B68EE', '#00FF7F', '#FF6B6B'];

    for (let i = 0; i < 40; i++) {
        const piece = document.createElement('div');
        const size = 5 + Math.random() * 7;
        piece.style.cssText = `
            position: absolute;
            width: ${size}px;
            height: ${size}px;
            background: ${colors[Math.floor(Math.random() * colors.length)]};
            left: ${10 + Math.random() * 80}%;
            top: -10px;
            border-radius: ${Math.random() > 0.5 ? '50%' : '2px'};
            animation: confettiFall ${1.5 + Math.random() * 2}s ease-in forwards;
            animation-delay: ${Math.random() * 0.6}s;
            opacity: 0;
        `;
        piece.style.animationFillMode = 'forwards';
        container.appendChild(piece);
    }

    document.body.appendChild(container);
    setTimeout(() => container.remove(), 4500);
}

/**
 * Handle social login
 */
function handleSocialLogin(provider) {
    try {
        showToast(t('auth.connectingTo', { provider }), 'info');
        import('../config.js').then(({ CONFIG }) => {
            window.location.href = `${CONFIG.API_BASE_URL}/auth/${provider}`;
        });
    } catch (error) {
        showToast(t('auth.socialLoginFailed'), 'error');
    }
}

/**
 * Handle authentication errors with proper formatting
 */
function handleAuthError(response) {
    if (response.errors && Array.isArray(response.errors)) {
        const errorMessages = response.errors.map(err => err.msg);
        if (errorMessages.length === 1) {
            showToast(errorMessages[0], 'error');
        } else {
            const formattedErrors = errorMessages.map((msg, index) => `${index + 1}. ${msg}`).join('\\n');
            showToast(`${t('auth.fixFollowing')}\\n${formattedErrors}`, 'error');
        }
    } else {
        showToast(response.message || t('auth.authFailed'), 'error');
    }
}

/**
 * Logout user
 */
async function logout() {
    if (confirm(t('auth.logoutConfirm'))) {
        // Disconnect Socket.io
        disconnectSocket();

        // Clear polling if active
        if (AppState.pollingInterval) {
            clearInterval(AppState.pollingInterval);
            updateState({ pollingInterval: null });
        }

        // Invalidate refresh token on the server
        try {
            await AuthAPI.logout();
        } catch (err) {
            // Continue with local cleanup even if server call fails
            console.warn('Server logout failed:', err);
        }

        // Clear storage (refresh token cookie is cleared by the server)
        localStorage.removeItem('currentUser');

        // Reset state
        updateState({
            currentUser: null,
            authToken: null
        });

        // Reload page to go back to login
        window.location.reload();
    }
}

// Export functions that need to be globally available
window.logout = logout;
